import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { setParcelsForStagesInStore } from '../../actions/parcels';
import { updateToastInfo } from '../../actions/settings';
import { updateSpinnerState } from '../../actions/spinner';
import ParcelStatusPage from '../../components/common/parcelStatusPages';
import DefectivePickupRescheduleModal from '../../components/common/defectivePickupPendingSchedule';
import {
  getParcelsByStatusAndSubStatus,
  updateOrdersDataWithInfo,
} from '../../parse-functions/parcels';
import { Routes } from '../../router/routes';
import { convertArrayToObj } from '../../utils/json';
import { returnWarehouseDropdownOptions } from '../../utils/warehouse';
import { setFiltersInStore } from '../../actions/filters';
import { returnEndOfDay, returnStartOfDay } from '../../utils/datetime';
import { replacementPendingRegisterTableColumns } from './utils';

const ReplacementScheduledList = () => {
  const pageStatus = 'replacementScheduled';
  const { t } = useTranslation();
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const otherWarehouses = useSelector(
    (state) => state?.user?.otherWarehousesInZone || {}
  );

  const breadCrumbItems = [
    {
      title: t('Defective - Register'),
      link: Routes.Defective.Register.path,
    },
  ];
  const dispatch = useDispatch();

  const [selectedParcelId, setSelectedParcelId] = useState(null);
  const storeCode = useSelector(
    (state) => state?.user?.warehouse?.warehouseCode || ''
  );
  const parcels = useSelector((state) => state?.parcels?.[pageStatus] || {});
  const filtersValue = useSelector((state) => state.filters?.[pageStatus]);
  const zone = useSelector((state) => state?.user?.warehouse?.zone || '');

  const handleFiltersSubmit = (startDate, endDate) => {
    dispatch(
      setFiltersInStore({
        pageType: pageStatus,
        data: {
          startDate: new Date(startDate),
          endDate: new Date(endDate),
        },
      })
    );
    getAvailableParcels(startDate, endDate);
  };

  const getAvailableParcels = async (startDate, endDate) => {
    dispatch(updateSpinnerState(true));
    try {
      const filters = {
        subStatus: ['defectivepending', 'defectivepicked', 'returnok'],
        isRescheduled: 'true',
        storeCode,
        startDate: new Date(startDate),
        endDate: new Date(endDate),
        zone,
      };
      const parcels = await getParcelsByStatusAndSubStatus(filters);
      dispatch(
        setParcelsForStagesInStore(
          pageStatus,
          convertArrayToObj(JSON.parse(JSON.stringify(parcels)), 'objectId')
        )
      );
      dispatch(updateSpinnerState(false));
      dispatch(
        updateToastInfo({
          show: true,
          type: 'success',
          title: t('Orders fetched'),
          message: t(
            'You can print challans your parcels here for which replacement is scheduled'
          ),
        })
      );
    } catch (e) {
      dispatch(updateSpinnerState(false));
      dispatch(
        updateToastInfo({
          show: true,
          type: 'danger',
          title: t('Error'),
          message: t(e.message),
        })
      );
    }
  };
  const onRowSelect = (value, objectId) => {
    if (selectedParcelId) {
      dispatch(
        updateToastInfo({
          show: true,
          type: 'danger',
          title: t('Only 1 item allowed'),
          message: t('Please deselect other item before choosing this'),
        })
      );
    } else {
      if (value) {
        setSelectedParcelId(objectId);
      } else {
        setSelectedParcelId(null);
      }
    }
  };
  const onRefreshClick = () => {
    const currentStartDate = filtersValue.startDate
      ? new Date(filtersValue.startDate)
      : returnStartOfDay(new Date());
    const currentEndDate = filtersValue.endDate
      ? new Date(filtersValue.endDate)
      : returnEndOfDay(new Date());
    getAvailableParcels(currentStartDate, currentEndDate);
  };

  const onRescheduleConfirm = async (data) => {
    dispatch(updateSpinnerState(true));
    try {
      const formData = {
        data: {
          challanNumber: parcels[selectedParcelId]?.challanNumber,
          ...data,
        },
        objectId: selectedParcelId,
      };
      await updateOrdersDataWithInfo(formData);
      dispatch(
        updateToastInfo({
          show: true,
          type: 'success',
          title: t('Replacement Item Received'),
          message: t(
            "This parcel's Replacement item has been marked as received Successfully"
          ),
        })
      );
      dispatch(updateSpinnerState(false));
    } catch (e) {
      dispatch(updateSpinnerState(false));
    }
  };
  const printChallanDetails = async () => {
    // write code to go to new page and print the challan
    window.open(
      `${window.location.origin}/challan/print/${selectedParcelId}`,
      '_blank'
    );
  };
  const handleShowRescheduleModal = () => {
    setShowRescheduleModal(false);
  };
  const handleItemBulkUpdate = (actionId) => {
    if (selectedParcelId) {
      switch (actionId) {
        case 'print': {
          printChallanDetails();
          break;
        }
        case 'schedule': {
          setShowRescheduleModal(true);
          break;
        }
        default: {
          dispatch(
            updateToastInfo({
              show: true,
              type: 'danger',
              title: t('Select an Action'),
              message: t('Please select an action from the dropdown'),
            })
          );
          break;
        }
      }
    } else {
      dispatch(
        updateToastInfo({
          show: true,
          type: 'danger',
          title: t('Select Item'),
          message: t('Please select an item to take action'),
        })
      );
    }
  };
  return (
    <>
      <ParcelStatusPage
        pageType={pageStatus}
        filtersValue={filtersValue}
        filterSubmit={handleFiltersSubmit}
        tableData={parcels}
        tableColumns={replacementPendingRegisterTableColumns(onRowSelect)}
        handleRefresh={onRefreshClick}
        pageTitle={t('Defective Scheduled')}
        breadCrumbItems={breadCrumbItems}
        pageSubTitle={t(
          'All the parcels for which the defective has been logged and scheduled for pickup for other warehouse'
        )}
        searchPlaceHolder={t('Search by Invoice No. in Database')}
        handleItemBulkUpdate={handleItemBulkUpdate}
        bulkActionOptions={[
          {
            label: 'Print',
            value: 'print',
          },
        ]}
        enableBulkActions={selectedParcelId && Object.keys(parcels)?.length > 0}
      />
      <DefectivePickupRescheduleModal
        disableTargetDropdown={false}
        targetDropdownOptions={returnWarehouseDropdownOptions(otherWarehouses)}
        selectedParcelId={selectedParcelId}
        pageStatus={pageStatus}
        showModal={showRescheduleModal}
        closeModal={handleShowRescheduleModal}
        onConfirm={onRescheduleConfirm}
      />
    </>
  );
};
export default ReplacementScheduledList;
